import { defineStore } from "pinia";
import { useAuthStore } from "./auth.store";
import FiltersService from "@/services/FiltersService";
import { userActivityEventKey } from "@/constants/userActivityEventTypes";
import { i18n } from "@/main";

/**
 * Zwraca domyślny zakres dat filtra — ostatnie 24 godziny.
 */
function defaultRange() {
  const now = new Date();
  const dayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  return { dateTimeStart: dayAgo, dateTimeEnd: now };
}

/**
 * Store filtrów widoku „Historia aktywności użytkowników”.
 *
 * Rozróżnia dwa stany:
 *  - `form`    — to, co użytkownik aktualnie ustawia w formularzu,
 *  - `applied` — kryteria zamrożone w chwili kliknięcia „Szukaj”.
 *
 * Dzięki temu zmiana pola formularza nie przestawia wyników ani paginacji
 * dopóki użytkownik ponownie nie wyszuka.
 */
export const useUserActivityFiltersStore = defineStore({
  id: "userActivityFilter",
  state: () => ({
    form: {
      userId: "",
      eventType: "",
      ...defaultRange(),
    },
    applied: null,          // Kryteria ostatniego wyszukiwania (null = jeszcze nie szukano).
    users: [],              // Lista użytkowników do selecta.
    eventTypeIds: [],       // Identyfikatory typów zdarzeń włączonych na backendzie (etykiety liczy i18n).
    valuesLoaded: false,    // Czy wartości filtrów zostały już pobrane z API.
    requireFields: [],      // Nazwy pól, które nie przeszły walidacji.
    error: false,           // Czy formularz zawiera błąd.
  }),
  actions: {
    /**
     * Pobiera z API wartości do wypełnienia selectów (użytkownicy + typy zdarzeń).
     */
    async loadFilterValues() {
      if (this.valuesLoaded) {
        return;
      }

      try {
        const data = await FiltersService.getUserActivityFilterValues();

        const usersResponse = Array.isArray(data?.users) ? data.users : [];
        this.users = usersResponse.map((user) => ({ id: user.id, name: user.username }));

        const enabledEventIds = Object.keys(data?.eventTypes ?? {})
          .map((value) => Number(value))
          .filter((value) => !Number.isNaN(value));

        // Trzymamy same identyfikatory — etykietę select renderuje przez i18n, więc
        // przełączenie języka nie wymaga ponownego pobrania wartości filtrów.
        this.eventTypeIds = enabledEventIds.filter((eventId) => userActivityEventKey(eventId));

        // Jeżeli wybrany wcześniej typ zdarzenia został wyłączony na backendzie — czyścimy wybór.
        if (this.form.eventType && !enabledEventIds.includes(Number(this.form.eventType))) {
          this.form.eventType = "";
        }

        this.valuesLoaded = true;
      } catch (e) {
        console.error(e);
        this.setError(i18n.global.t("activity-filter-values-error"));
      }
    },

    /**
     * Waliduje formularz. Wypełnia `requireFields` oraz `error`.
     *
     * @returns {boolean} true, gdy formularz jest poprawny.
     */
    validate() {
      this.requireFields = [];
      this.error = false;

      if (!this.form.dateTimeStart) {
        this.requireFields.push("dateTimeStart");
      }
      if (!this.form.dateTimeEnd) {
        this.requireFields.push("dateTimeEnd");
      }
      if (
        this.form.dateTimeStart &&
        this.form.dateTimeEnd &&
        new Date(this.form.dateTimeEnd) <= new Date(this.form.dateTimeStart)
      ) {
        this.requireFields.push("dateTimeStart", "dateTimeEnd");
        this.error = true;
        this.setError(i18n.global.t("activity-date-range-invalid"));
        return false;
      }

      if (this.requireFields.length) {
        this.error = true;
        this.setError(i18n.global.t("required-parameter-missing"));
        return false;
      }

      return true;
    },

    /**
     * Zamraża bieżący formularz jako kryteria wyszukiwania.
     *
     * @returns {object|null} Zastosowane kryteria lub null, gdy formularz jest niepoprawny.
     */
    apply() {
      if (!this.validate()) {
        return null;
      }

      this.applied = {
        userId: this.form.userId === "" ? null : Number(this.form.userId),
        eventType: this.form.eventType === "" ? null : Number(this.form.eventType),
        dateTimeStart: new Date(this.form.dateTimeStart).toISOString(),
        dateTimeEnd: new Date(this.form.dateTimeEnd).toISOString(),
      };

      return this.applied;
    },

    /**
     * Przywraca formularz do stanu domyślnego. Nie kasuje wyników ostatniego wyszukiwania.
     */
    resetForm() {
      this.form = { userId: "", eventType: "", ...defaultRange() };
      this.requireFields = [];
      this.error = false;
    },

    setError(message) {
      useAuthStore().errorMessage = message;
    },
  },
});
